import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Logo } from '@/components/Logo';
import {
  LayoutDashboard, Users, Clock, CalendarDays,
  Wallet, FileText, LogOut, ScrollText, Settings, BarChart3, UserCircle, ShieldCheck, Coins, Moon, Sun,
  Building2, UserCheck, Receipt, CreditCard, Award, TrendingUp, ChevronDown, ChevronRight, FileBarChart, ReceiptText, UserPlus,
  Menu, X
} from 'lucide-react';
import { useState, useEffect } from 'react';

const adminLinks = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/employees', icon: Users, label: 'Employees' },
  { to: '/attendance', icon: Clock, label: 'Attendance' },
  { to: '/leaves', icon: CalendarDays, label: 'Leaves' },
  { to: '/payroll', icon: Wallet, label: 'Payroll' },
  { to: '/salary-templates', icon: Coins, label: 'Salary Components' },
  { to: '/joiners', icon: UserPlus, label: 'Joiners & Incentives' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/letters', icon: ScrollText, label: 'Letters' },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

const financeLinks = [
  { to: '/finance', icon: TrendingUp, label: 'Dashboard' },
  { to: '/finance/clients', icon: Building2, label: 'Clients' },
  { to: '/finance/placements', icon: UserCheck, label: 'Placements' },
  { to: '/finance/invoices', icon: Receipt, label: 'Invoices' },
  { to: '/finance/credit-notes', icon: ReceiptText, label: 'Credit Notes' },
  { to: '/finance/payments', icon: CreditCard, label: 'Payments' },
  { to: '/finance/incentives', icon: Award, label: 'Incentives' },
  { to: '/finance/reports', icon: FileBarChart, label: 'Reports' },
];

const employeeLinks = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/joiners', icon: UserPlus, label: 'My Joiners' },
  { to: '/attendance', icon: Clock, label: 'My Attendance' },
  { to: '/leaves', icon: CalendarDays, label: 'My Leaves' },
  { to: '/payslips', icon: FileText, label: 'My Payslips' },
  { to: '/kyc', icon: ShieldCheck, label: 'KYC Details' },
  { to: '/profile', icon: UserCircle, label: 'My Profile' },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [financeOpen, setFinanceOpen] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const links = user?.role === 'admin' ? adminLinks : employeeLinks;
  const showFinance = ['admin', 'accounts'].includes(user?.role);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile header bar */}
      <div className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between h-14 px-4 bg-card border-b border-border md:hidden" data-testid="mobile-header">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 text-foreground hover:bg-accent rounded-sm transition-colors"
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <Logo size="sm" />
        <div className="w-9" />
      </div>

      {/* Backdrop overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col shrink-0
          transform transition-transform duration-200 ease-in-out
          md:relative md:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="sidebar"
      >
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <Logo size="md" />
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 text-muted-foreground hover:text-foreground md:hidden"
            data-testid="sidebar-close-btn"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {links.map(link => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                }`
              }
            >
              <link.icon className="w-4 h-4" strokeWidth={1.5} />
              {link.label}
            </NavLink>
          ))}

          {showFinance && (
            <>
              <div className="pt-3 pb-1">
                <button
                  onClick={() => setFinanceOpen(!financeOpen)}
                  className="flex items-center justify-between w-full px-3 py-1.5 text-[10px] uppercase tracking-[0.15em] font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="finance-section-toggle"
                >
                  Finance
                  {financeOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>
              </div>
              {financeOpen && financeLinks.map(link => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === '/finance'}
                  data-testid={`nav-${link.to.slice(1).replace('/', '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    }`
                  }
                >
                  <link.icon className="w-4 h-4" strokeWidth={1.5} />
                  {link.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-border">
          <button
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
            className="flex items-center gap-2 w-full px-3 py-2 mb-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent rounded-sm transition-all duration-200"
          >
            {theme === 'dark' ? (
              <>
                <Sun className="w-4 h-4" strokeWidth={1.5} />
                Light Mode
              </>
            ) : (
              <>
                <Moon className="w-4 h-4" strokeWidth={1.5} />
                Dark Mode
              </>
            )}
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold font-heading">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate font-body">{user?.name}</p>
              <p className="text-[11px] text-muted-foreground capitalize">{user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-sm transition-all duration-200"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto bg-background">
        <div className="p-4 pt-18 md:p-6 md:pt-6 lg:p-8 page-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
